import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './GIS.css';
import dptosGeoJson from '../shapes/Dptos.geojson';
import distritosGeoJson from '../shapes/Distritos.geojson';
import isoietasFebrero2026GeoJson from '../shapes/Isoietas Febrero 2026.geojson';
import isoietasMarzo2026GeoJson from '../shapes/Isoietas Marzo 2026.geojson';
import precipitacionesFebrero2026GeoJson from '../shapes/Precipitaciones Febrero 2026.geojson';
import precipitacionesMarzo2026GeoJson from '../shapes/Precipitaciones Marzo 2026.geojson';
import riesgosDeInundacionGeoJson from '../shapes/Riegos de Inundacion.geojson';
import riosDrhGeoJson from '../shapes/RiosDRH.geojson';

const geoJsonLayers = [
  {
    name: 'Departamentos',
    dataPath: dptosGeoJson,
    color: '#f97316',
    fillOpacity: 0.08
  },
  {
    name: 'Distritos',
    dataPath: distritosGeoJson,
    color: '#7c3aed',
    fillOpacity: 0.1
  },
  {
    name: 'Rios DRH',
    dataPath: riosDrhGeoJson,
    color: '#2563eb',
    fillOpacity: 0.2
  },
  {
    name: 'Riesgos de inundacion',
    dataPath: riesgosDeInundacionGeoJson,
    color: '#dc2626',
    fillOpacity: 0.22
  },
  {
    name: 'Precipitaciones febrero 2026',
    dataPath: precipitacionesFebrero2026GeoJson,
    color: '#0f766e',
    fillOpacity: 0.2
  },
  {
    name: 'Precipitaciones marzo 2026',
    dataPath: precipitacionesMarzo2026GeoJson,
    color: '#0891b2',
    fillOpacity: 0.2
  },
  {
    name: 'Isoietas febrero 2026',
    dataPath: isoietasFebrero2026GeoJson,
    color: '#ca8a04',
    fillOpacity: 0.08
  },
  {
    name: 'Isoietas marzo 2026',
    dataPath: isoietasMarzo2026GeoJson,
    color: '#16a34a',
    fillOpacity: 0.08
  }
];

const formatPropertyLabel = (key) =>
  key
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const popupFieldsByLayer = {
  Departamentos: ['NAME'],
  Distritos: ['NAME', 'NOMBRE', 'RESPONSABL'],
  'Rios DRH': ['nombre', 'fuente']
};

const buildFeaturePopup = (layerName, feature) => {
  const properties = feature?.properties ?? {};
  const preferredFields = popupFieldsByLayer[layerName];
  const entries = preferredFields
    ? preferredFields
        .filter((key) => properties[key] !== null && properties[key] !== undefined && properties[key] !== '')
        .map((key) => [key, properties[key]])
    : Object.entries(properties).filter(([, value]) => value !== null && value !== '');

  if (entries.length === 0) {
    return `<strong>${layerName}</strong>`;
  }

  const items = entries
    .slice(0, 8)
    .map(
      ([key, value]) => `<div><strong>${formatPropertyLabel(key)}:</strong> ${String(value)}</div>`
    )
    .join('');

  return `<strong>${layerName}</strong>${items}`;
};

const createGeoJsonLayer = ({ name, data, color, fillOpacity }) =>
  {
    const layer = L.geoJSON(null, {
      style: () => ({
        color,
        weight: 2,
        opacity: 0.9,
        fillColor: color,
        fillOpacity
      }),
      pointToLayer: (_feature, latlng) =>
        L.circleMarker(latlng, {
          radius: 6,
          color,
          weight: 2,
          fillColor: color,
          fillOpacity: 0.85
        }),
      onEachFeature: (feature, featureLayer) => {
        featureLayer.bindPopup(buildFeaturePopup(name, feature));
      }
    });

    const features = Array.isArray(data?.features) ? data.features : [];

    features.forEach((feature, index) => {
      try {
        layer.addData(feature);
      } catch (error) {
        console.warn(`Feature omitida en capa ${name} (indice ${index})`, {
          error,
          feature
        });
      }
    });

    return layer;
  };

const loadGeoJsonData = async (dataPath) => {
  const response = await fetch(dataPath);

  if (!response.ok) {
    throw new Error(`No se pudo cargar ${dataPath}: ${response.status}`);
  }

  return response.json();
};

function SIG() {
  const mapRef = useRef(null);

  useEffect(() => {
    let map = null;
    const mapContainer = mapRef.current;

    if (!mapContainer) {
      return () => {};
    }

    try {
      mapContainer.innerHTML = '';
      mapContainer.removeAttribute('data-leaflet-id');
      mapContainer._leaflet_id = null;

      map = L.map(mapContainer, {
        center: [-26.8083, -65.2176],
        zoom: 10,
        zoomControl: true
      });

      const argenMapLayer = L.tileLayer(
        'https://wms.ign.gob.ar/geoserver/gwc/service/wmts?REQUEST=GetTile&SERVICE=WMTS&VERSION=1.0.0&LAYER=capabaseargenmap&STYLE=&TILEMATRIX=EPSG:3857:{z}&TILEMATRIXSET=EPSG:3857&FORMAT=image/png&TILECOL={x}&TILEROW={y}',
        {
          attribution: '&copy; IGN Argentina',
          maxZoom: 18,
          tileSize: 256
        }
      );

      const satelliteLayer = L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        {
          attribution: '&copy; Esri | &copy; IGN Argentina',
          maxZoom: 18
        }
      );

      const osmLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: 19
      });

      argenMapLayer.addTo(map);

      const overlays = {};
      const deferredOverlays = new Map();

      geoJsonLayers.forEach(({ name, dataPath, color, fillOpacity }) => {
        const placeholderLayer = L.layerGroup();
        deferredOverlays.set(placeholderLayer, {
          config: { name, dataPath, color, fillOpacity },
          loaded: false,
          loading: false,
          layerInstance: null
        });
        overlays[name] = placeholderLayer;
      });

      L.control
        .layers(
          {
            ArgenMap: argenMapLayer,
            'Vista Satelital': satelliteLayer,
            OpenStreetMap: osmLayer
          },
          overlays,
          {
            collapsed: false
          }
        )
        .addTo(map);

      map.on('overlayadd', async (event) => {
        const overlayState = deferredOverlays.get(event.layer);

        if (!overlayState) {
          return;
        }

        if (!overlayState.loaded && !overlayState.loading) {
          overlayState.loading = true;

          try {
            const data = await loadGeoJsonData(overlayState.config.dataPath);
            overlayState.layerInstance = createGeoJsonLayer({
              ...overlayState.config,
              data
            });
            overlayState.loaded = true;
          } catch (error) {
            console.error(`Error cargando la capa ${overlayState.config.name}:`, error);
          } finally {
            overlayState.loading = false;
          }
        }

        if (
          overlayState.layerInstance &&
          !event.layer.hasLayer(overlayState.layerInstance)
        ) {
          event.layer.addLayer(overlayState.layerInstance);
        }
      });

      map.on('overlayremove', (event) => {
        const overlayState = deferredOverlays.get(event.layer);

        if (!overlayState?.layerInstance) {
          return;
        }

        if (event.layer.hasLayer(overlayState.layerInstance)) {
          event.layer.removeLayer(overlayState.layerInstance);
        }
      });

      const officeIcon = L.divIcon({
        className: 'gis-marker-office',
        html: '<span></span>',
        iconSize: [18, 18],
        iconAnchor: [9, 9]
      });

      L.marker([-26.83842732564927, -65.21623678615293], { icon: officeIcon })
        .addTo(map)
        .bindPopup(
          '<strong>Oficina Central DPA</strong><br/><span>Direccion Provincial de Agua - San Miguel de Tucuman</span>'
        );

      L.control.scale({ position: 'bottomleft', imperial: false }).addTo(map);
    } catch (error) {
      // Keep a visible fallback in the UI while also logging the real cause for debugging.
      console.error('Error inicializando el visor GIS:', error);
      mapContainer.innerHTML =
        '<div class="gis-map-error">Error inicializando el mapa. Recargue la pagina.</div>';
    }

    return () => {
      if (map) {
        map.remove();
      }
      if (mapContainer) {
        mapContainer.innerHTML = '';
        mapContainer.removeAttribute('data-leaflet-id');
        if (mapContainer._leaflet_id) {
          delete mapContainer._leaflet_id;
        }
      }
    };
  }, []);

  return (
    <main className="gis-page">
      <section className="gis-hero">
        <div className="gis-shell reveal-on-scroll">
          <h1>Sistema de Informacion Geografica</h1>
          <p>
            Visualice proyectos, obras hidraulicas y puntos de interes de la Direccion Provincial del
            Agua en la provincia de Tucuman.
          </p>
        </div>
      </section>

      <section className="gis-content">
        <div className="gis-shell gis-grid">
          <article className="gis-panel gis-info reveal-on-scroll">
            <h2>Visor de Proyectos y Obras</h2>
            <p>
              Explore las zonas donde la Direccion Provincial del Agua desarrolla infraestructura
              hidrica, obras de defensa y gestion de recursos hidricos.
            </p>

            <h3>Referencias</h3>
            <ul>
              <li><span className="legend-dot office" /> Oficina Central</li>
              <li><span className="legend-dot district" /> Departamentos y distritos</li>
              <li><span className="legend-dot water" /> Rios e isoietas</li>
              <li><span className="legend-dot risk" /> Riesgos y precipitaciones</li>
            </ul>
          </article>

          <article className="gis-panel gis-map reveal-on-scroll">
            <div ref={mapRef} className="gis-map-canvas">
              <div className="gis-map-loading">Cargando mapa...</div>
            </div>
          </article>
        </div>
      </section>
    </main>
  );
}

export default SIG;
